import { styled } from '@mui/material/styles'
import { differenceInMinutes } from 'date-fns'
import { useMemo } from 'react'
import { useDateFormat } from '../../../../../../hooks'
import { theme } from '../../../../../../modules/App/Providers/Theme/theme'
import { Spacer } from '../../../../../Spacer'
import { Title } from '../../../../../schedule/Schedule/Content/Time/Content/Availabilities/Title'
import { Item } from './Item'
import { MergedAvailabilities, ResourceAvailability } from '../../../../../../types/mergedAvailabilities'
import { useTranslation } from 'react-i18next'

interface Props {
  availabilities: MergedAvailabilities
  dateStart?: Date
}

const Container = styled('div')<{
  isFitterSchedulingMode?: boolean
}>`
  align-content: start;
  color: ${theme.palette.grey[500]};
  display: grid;
  font-size: 0.75rem;
  list-style: none;
  margin: 0;
  padding: 0;
  position: relative;
  row-gap: 1rem;
  text-align: center;

  @media (min-width: ${theme.breakpoints.values.md}px) {
    height: 17rem;
    overflow-y: auto;
  }
`

const Items = ({ availabilities, dateStart }: Props) => {
  const { t } = useTranslation()

  const { dateTo24hrDisplayTime, dateToISODateKey } = useDateFormat()
  const selectedDay = dateStart as Date
  const dayKey = dateToISODateKey(selectedDay)

  const rawSlots = availabilities?.[dayKey]

  // Deduplicate slots by time - only show one slot per time
  const deduplicatedSlots = useMemo(() => {
    if (!rawSlots?.length) return []

    const timeToSlotMap = new Map<string, ResourceAvailability>()

    rawSlots.forEach((slot) => {
      const timeKey = slot.date_start.toISOString()
      // Only add if we haven't seen this time yet - keep the first occurrence
      if (!timeToSlotMap.has(timeKey)) {
        timeToSlotMap.set(timeKey, slot)
      }
    })

    return Array.from(timeToSlotMap.values())
  }, [rawSlots])

  const hasAvailabilitiesForSelectedDay = Boolean(deduplicatedSlots?.length)

  if (!hasAvailabilitiesForSelectedDay) {
    return <Container>{t('common.label.noAvailabilitiesForThisDay')}</Container>
  }

  const TIME_SLOT_DATA_ATTRIBUTE = 'data-time-slots'

  return (
    <>
      {hasAvailabilitiesForSelectedDay && <Title hideSpecialistTime />}
      <Spacer count={0.5} />
      <div {...{ [TIME_SLOT_DATA_ATTRIBUTE]: true }} style={{ position: 'relative', height: 1, top: '4rem', zIndex: 0 }} />
      <Container as="ul">
        {deduplicatedSlots?.map((slot) => {
          const duration = differenceInMinutes(slot.date_end, slot.date_start)
          const key = `${dateTo24hrDisplayTime(slot.date_start)}${duration}`

          return <Item slot={slot} key={key} />
        })}
      </Container>
    </>
  )
}

export { Items }
